import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Typography, Alert, Spin } from 'antd';
import { Lock, CircleCheck, CircleAlert } from 'lucide-react';
import Icon from '../../components/Icon';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyResetToken, resetPassword } from '../../auth/passwordReset.service';
import { brandingConfig } from '../../theme/brandingConfig';

const { Title, Text } = Typography;

const containerStyle = {
  height: '100dvh',
  minHeight: '100%',
  padding: 16,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const bgStyle = {
  content: '""',
  display: 'block',
  position: 'absolute',
  zIndex: -1,
  inset: 0,
  backgroundImage: `url(${brandingConfig.assets.emailBackground})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 450,
  boxShadow: 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      setVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const result = await verifyResetToken(token);
        if (result.valid) {
          setTokenValid(true);
        } else {
          setError('This reset link has expired or is invalid. Please request a new password reset.');
        }
      } catch (err) {
        setError('Failed to verify reset link. Please try again or request a new password reset.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordError(validatePassword(newPassword));

    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    if (password !== newConfirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    const passwordValidationError = validatePassword(password);

    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (verifying) {
    return (
      <div style={containerStyle}>
        <div style={bgStyle} />
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin size="large" style={{ marginBottom: 16 }} />
            <div>
              <Text>Verifying Reset Link...</Text>
            </div>
            <Text type="secondary">
              Please wait while we verify your password reset link.
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!tokenValid && !success) {
    return (
      <div style={containerStyle}>
        <div style={bgStyle} />
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Icon as={CircleAlert} size={48} strokeWidth={2.5} style={{ color: '#E68A4C', marginBottom: 16 }} />
            <Title level={5} type="danger">
              Invalid Reset Link
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              {error || 'This password reset link is invalid or has expired.'}
            </Text>
            <Button type="primary" onClick={handleBackToLogin} block>
              Back to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div style={containerStyle}>
        <div style={bgStyle} />
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Icon as={CircleCheck} size={48} strokeWidth={2.5} style={{ color: '#9FBDB1', marginBottom: 16 }} />
            <Title level={5} style={{ color: '#9FBDB1' }}>
              Password Reset Successful!
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Your password has been successfully reset. You can now sign in with your new password.
            </Text>
            <Button type="primary" onClick={handleBackToLogin} block>
              Sign In Now
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={bgStyle} />
      <Card style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <Icon as={Lock} size={24} />
            <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
              Reset Your Password
            </Title>
          </div>
          <Text type="secondary">
            Enter your new password below
          </Text>
        </div>

        {error && (
          <Alert type="error" message={error} style={{ marginBottom: 16 }} />
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <Text>New Password</Text>
            <Input.Password
              value={password}
              onChange={handlePasswordChange}
              status={passwordError ? 'error' : ''}
              autoFocus
              style={{ marginTop: 4 }}
            />
            <Text type={passwordError ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {passwordError || 'Minimum 6 characters required'}
            </Text>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text>Confirm New Password</Text>
            <Input.Password
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              status={confirmPasswordError ? 'error' : ''}
              style={{ marginTop: 4 }}
            />
            <Text type={confirmPasswordError ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {confirmPasswordError || 'Re-enter your new password'}
            </Text>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={loading}
            style={{ marginBottom: 8, fontWeight: 500 }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </Button>

          <Button type="link" block onClick={handleBackToLogin}>
            Back to Sign In
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
